/**
 * Secure Fetch Utilities
 *
 * Provides fetch wrappers that automatically include CSRF tokens
 * using the double-submit cookie pattern. The CSRF token is read
 * from the `csrf_token` cookie and sent in the `x-csrf-token` header.
 *
 * SECURITY: These utilities address OWASP A01:2021 (Broken Access Control)
 * by ensuring all state-changing requests include CSRF protection.
 *
 * @see src/api/middleware/csrf.ts for server-side validation
 */

// Cookie name must match server-side constant
const CSRF_COOKIE_NAME = 'csrf_token'
const CSRF_HEADER_NAME = 'x-csrf-token'

/**
 * Extracts CSRF token from document cookies.
 *
 * The token is set by the server as a non-HttpOnly cookie
 * (intentionally readable by JavaScript for the double-submit pattern).
 *
 * @returns The CSRF token if found, empty string otherwise
 */
export function getCSRFToken(): string {
	if (typeof document === 'undefined') {
		// SSR or non-browser environment
		return ''
	}

	const cookies = document.cookie.split(';')
	for (const cookie of cookies) {
		const trimmed = cookie.trim()
		if (trimmed.startsWith(`${CSRF_COOKIE_NAME}=`)) {
			return trimmed.slice(CSRF_COOKIE_NAME.length + 1)
		}
	}
	return ''
}

/**
 * Type for secure fetch options.
 * Extends RequestInit but ensures credentials are always included.
 */
export type SecureFetchOptions = Omit<RequestInit, 'credentials'> & {
	/** Additional headers to merge with CSRF header */
	headers?: HeadersInit
}

/**
 * Fetch wrapper that automatically includes CSRF token and credentials.
 *
 * Use this for any state-changing request (POST, PUT, PATCH, DELETE).
 * GET requests don't require CSRF tokens but this wrapper still works for them.
 *
 * @param url - The URL to fetch
 * @param options - Fetch options (credentials are always 'include')
 * @returns Promise resolving to the Response
 *
 * @example
 * ```ts
 * const response = await secureFetch('/api/blocks/123', {
 *   method: 'DELETE',
 * })
 * ```
 */
export async function secureFetch(
	url: string,
	options: SecureFetchOptions = {}
): Promise<Response> {
	const csrfToken = getCSRFToken()
	const { headers: userHeaders, ...rest } = options

	// Merge user headers with CSRF header
	const headers: Record<string, string> = {}

	// Add user-provided headers first
	if (userHeaders) {
		if (userHeaders instanceof Headers) {
			userHeaders.forEach((value, key) => {
				headers[key] = value
			})
		} else if (Array.isArray(userHeaders)) {
			for (const [key, value] of userHeaders) {
				headers[key] = value
			}
		} else {
			Object.assign(headers, userHeaders)
		}
	}

	// Add CSRF token header (overwrites if somehow already set)
	if (csrfToken) {
		headers[CSRF_HEADER_NAME] = csrfToken
	}

	return fetch(url, {
		...rest,
		credentials: 'include',
		headers,
	})
}

/**
 * Type for secure JSON fetch options.
 * Body can be any JSON-serializable value.
 */
export type SecureJsonFetchOptions = Omit<SecureFetchOptions, 'body'> & {
	/** JSON body to serialize and send */
	body?: unknown
}

/**
 * JSON-specific fetch wrapper with CSRF protection.
 *
 * Automatically sets Content-Type to application/json and
 * stringifies the body. Use this for all JSON API calls.
 *
 * @param url - The URL to fetch
 * @param options - Fetch options with optional body to stringify
 * @returns Promise resolving to the Response
 *
 * @example
 * ```ts
 * const response = await secureJsonFetch('/api/blocks', {
 *   method: 'POST',
 *   body: { type: 'player', position_x: 100 },
 * })
 * ```
 */
export async function secureJsonFetch(
	url: string,
	options: SecureJsonFetchOptions = {}
): Promise<Response> {
	const { body, headers: userHeaders, ...rest } = options

	// Build headers with Content-Type
	const headers: Record<string, string> = {
		'Content-Type': 'application/json',
	}

	// Add user-provided headers
	if (userHeaders) {
		if (userHeaders instanceof Headers) {
			userHeaders.forEach((value, key) => {
				headers[key] = value
			})
		} else if (Array.isArray(userHeaders)) {
			for (const [key, value] of userHeaders) {
				headers[key] = value
			}
		} else {
			Object.assign(headers, userHeaders)
		}
	}

	return secureFetch(url, {
		...rest,
		headers,
		body: body !== undefined ? JSON.stringify(body) : undefined,
	})
}
